// Popover content for Signal Detection fields

export const firstPersonPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Detects first-person storytelling.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      First-person perspective helps flag content that feels more emotionally
      immersive, which can amplify the impact of inappropriate or suggestive
      material.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        <strong>True:</strong> "I remember the way the gym lights flickered that
        day. My heart was pounding as I stepped onto the court — not because of
        the game, but because she was watching."
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>False:</strong> "He remembered the way the gym lights flickered
        that day. His heart was pounding as he stepped onto the court — not
        because of the game, but because she was watching."
      </div>
    </div>
  </div>
);

export const physicalContactPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Detects touching, intimacy, kissing
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Captures implied physical contact to distinguish casual, platonic, or
      emotionally charged moments—essential for flagging romantic or suggestive
      subtext in otherwise neutral narratives. It also supports downstream
      reasoning about tone, age-appropriateness, or cultural sensitivity,
      especially when romance or intimacy is not made explicit.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <strong>Romantic Touch:</strong>
      "As they walked through the garden, his fingers lingered briefly on hers
      before letting go."
      <div style={{ marginTop: 8 }}>
        <strong>No Contact:</strong> "They exchanged smiles from across the
        lecture hall, never approaching one another."
      </div>
    </div>
  </div>
);

export const settingPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Identifies scene location
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Identifying the setting helps assess the appropriateness of suggestive
      content based on where it's taking place.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "She waited by the lockers, clutching her books as the bell rang."
      </div>
      <div style={{ marginTop: 8 }}>
        "He sat on the edge of the bed, staring at the posters on the wall."
      </div>
    </div>
  </div>
);

export const explicitTermsFoundPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Detects use of direct sexual terms
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Flags clear sexual language that may be inappropriate without valid
      context, helping prioritize potentially risky content.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        <strong>True:</strong> "The novel referenced the 'climax' of a sexual
        encounter, using explicit detail to describe it."
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>False:</strong> "They discussed their favorite parts of the book
        in literature class."
      </div>
    </div>
  </div>
);

export const explicitTermsListPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      List of detected explicit terms
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Lets reviewers or later models see exactly what terms were used, aiding in
      transparency and context-based override decisions.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />-
    </div>
  </div>
);

export const anatomicalTermsPresentPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Looks for body-part references
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Surfaces content that includes potentially sensitive anatomical references
      that aid context-based review
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        <strong>True:</strong> "The diagram labeled parts of the female anatomy,
        including the br**sts"
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>False:</strong> "The lungs are responsible for oxygen exchange
        in the bloodstream."
      </div>
    </div>
  </div>
);

export const anatomicalTermsListPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Body-related terms detected
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Clarifies what anatomical references were made
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />-
    </div>
  </div>
);

export const containsDialoguePopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Checks for spoken interaction
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Detecting dialogue helps the pipeline distinguish between internal
      narration and direct character interaction, which is critical for
      identifying scenes where inappropriate behavior is communicated through
      spoken words.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        <strong>True:</strong> "She looked up and whispered, 'Do you think
        anyone saw us?'"
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>False:</strong> "He walked past her in silence, their eyes
        meeting briefly before he turned away."
      </div>
    </div>
  </div>
);

export const chromosomallyDefinedGenderPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      List of genders of persons explicitly described and identified
      chromosomally
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Detecting the gender of characters helps the system evaluate relationship
      dynamics and scene context.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "He had his shirt off at the festival and was waving his arms in the
        air"
        <br />→ Output: male
      </div>
    </div>
  </div>
);

export const selfIdentifiedGendersPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      List of self identified genders mentioned, even if chromosomally defined
      as well or implied
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Adds nuance, good to know...
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "Some days I feel more like a boy, other days more like a girl — and
        sometimes, neither really fits. I don’t expect everyone to get it, but
        this is who I am."
        <br />→ Output: genderfluid
      </div>
    </div>
  </div>
);
